import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ReferralsProvider, useReferrals } from '@/providers';
import {
  OnboardingPage,
  // DashboardPage,
  ReferralEntryPage,
  ReferralsPage,
  LeaderboardPage,
  EventsPage,
  // SettingsPage,
  // AllAlertsPage,
  AllPositionsPage,
  HowItWorksPage,
  SettingsPage,
  AlertsPage,
  StockAlertsPage,
  StatisticsPage,
  AnalyzePage,
  PriceAlertsPage,
  PriceAlertConfigPage,
  CopyTradingPage,
  NotificationsPage,
} from '@/pages';
import { useLaunchParams } from '@telegram-apps/sdk-react';

/**
 * Referral Routes - Wrapped with ReferralsProvider for shared state
 */
const ReferralRoutes: React.FC = () => {
  const { isWhitelisted, data } = useReferrals();

  return (
    <Routes>
      <Route path="/referral-entry" element={<ReferralEntryPage />} />
      <Route path="/referrals" element={<ReferralsPage />} />
      <Route path="/leaderboard" element={<LeaderboardPage />} />
      <Route path="/events" element={<EventsPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="/positions" element={<AllPositionsPage />} />
      <Route path="/alerts" element={<AlertsPage />} />
      <Route path="/alerts/stocks" element={<StockAlertsPage />} />
      <Route path="/price-alerts" element={<PriceAlertsPage />} />
      <Route path="/price-alerts/manage" element={<PriceAlertConfigPage />} />
      <Route path="/statistics" element={<StatisticsPage />} />
      <Route path="/analyze" element={<AnalyzePage />} />
      <Route path="/copy-trading" element={<CopyTradingPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      {/* Redirect to appropriate screen based on data availability */}
      <Route
        path="/*"
        element={
          data && isWhitelisted ? (
            <Navigate to="/alerts" replace />
          ) : data ? (
            <Navigate to="/referrals" replace />
          ) : (
            <Navigate to="/referral-entry" replace />
          )
        }
      />
    </Routes>
  );
};

/**
 * Main App Content - Handles routing and onboarding check
 */
const AppContent: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || '';
  // Check onboarding status synchronously to avoid race conditions
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(() => {
    const onboardingCompleted = localStorage.getItem(
      `onboarding-completed-${import.meta.env.VITE_APP_VERSION}`
    );
    if(userId === "5675692625") {
      return true;
    }
    return !!onboardingCompleted;
  });

  // Listen for onboarding completion and storage changes
  useEffect(() => {
    const handleOnboardingComplete = () => {
      const onboardingCompleted = localStorage.getItem(
        `onboarding-completed-${import.meta.env.VITE_APP_VERSION}`
      );
      setHasCompletedOnboarding(!!onboardingCompleted);
    };

    // Listen for custom event from OnboardingPage (same tab)
    window.addEventListener('onboarding-completed', handleOnboardingComplete);
    
    // Listen for storage changes (other tabs)
    window.addEventListener('storage', handleOnboardingComplete);
    
    return () => {
      window.removeEventListener('onboarding-completed', handleOnboardingComplete);
      window.removeEventListener('storage', handleOnboardingComplete);
    };
  }, []);

  return (
    <Routes>
      {/* Onboarding route */}
      <Route
        path="/onboarding"
        element={
          !hasCompletedOnboarding ? (
            <OnboardingPage />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Dashboard route (whitelisted users only) */}
      {/* <Route
        path="/dashboard"
        element={
          hasCompletedOnboarding && userId ? (
            <DashboardPage />
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      /> */}

      {/* Settings route (whitelisted users only) */}
      {/* <Route
        path="/settings"
        element={
          hasCompletedOnboarding && userId ? (
            <SettingsPage />
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      /> */}

      {/* All Alerts route (whitelisted users only) */}
      {/* <Route
        path="/all-alerts"
        element={
          hasCompletedOnboarding && userId ? (
            <AllAlertsPage />
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      /> */}

      {/* All Positions route (whitelisted users only) */}
      {/* <Route
        path="/all-positions"
        element={
          hasCompletedOnboarding && userId ? (
            <AllPositionsPage />
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      /> */}

      {/* Referral routes - wrapped with provider */}
      <Route
        path="/*"
        element={
          hasCompletedOnboarding ? (
            <ReferralsProvider>
              <ReferralRoutes />
            </ReferralsProvider>
          ) : (
            <Navigate to="/onboarding" replace />
          )
        }
      />
    </Routes>
  );
};

/**
 * App - Root component with BrowserRouter
 */
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;

